// Presupuesto (tope y consumido) y reglas de una promo de venta (por cliente, acumulable, forma de pago).
import type { ReactNode } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { cn } from '@/lib/cn';
import { FieldHelp } from '@/shared/patterns/EditorForm';
import { BudgetField } from '@/shared/patterns/Lifecycle';
import { SALE_PROMOTION_HELP } from '../constants/helpText';
import { defaultStackable, type SalePromotionFormValues } from '../lib/salePromotionForm';
import { STRIP_LABEL } from './editorParts';
import { PaymentOptionsPicker } from './PaymentOptionsPicker';

const BOX = 'rounded-md border border-border p-2.5';

/** Lo único que el tope necesita del formulario: venta y referido lo comparten. */
interface BudgetFormValues {
    budgetMax: string;
}
const HELP = 'mt-1 text-[10px] text-muted-foreground';
const CHECK = 'inline-flex cursor-pointer items-center gap-1.5 text-[11.5px]';

/** Lo tecleado en pesos, en centavos; `null` si no es un monto válido. */
function typedCents(text: string): number | null {
    const value = Number(text);
    return text.trim() !== '' && Number.isFinite(value) && value > 0
        ? Math.round(value * 100)
        : null;
}

function RuleBox({
    label,
    help,
    note,
    children,
}: {
    label: string;
    help: string;
    note: string;
    children: ReactNode;
}) {
    return (
        <div className={BOX}>
            <div className="mb-1.5 flex items-center gap-1">
                <p className={STRIP_LABEL}>{label}</p>
                <FieldHelp label={label} help={help} />
            </div>
            {children}
            <p className={HELP}>{note}</p>
        </div>
    );
}

function BudgetBox({ consumedCents }: { consumedCents: number | null }) {
    const { control } = useFormContext<BudgetFormValues>();
    return (
        <Controller
            control={control}
            name="budgetMax"
            render={({ field, fieldState }) => (
                <BudgetField
                    id="promo-budget"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    error={fieldState.error?.message}
                    maxCents={typedCents(field.value)}
                    consumedCents={consumedCents}
                />
            )}
        />
    );
}

function PaymentOptionsBox() {
    const { control, setValue } = useFormContext<SalePromotionFormValues>();
    const selected = useWatch({ control, name: 'paymentOptions' });
    return (
        <div className={cn(BOX, 'mt-2')}>
            <div className="mb-1.5 flex items-baseline justify-between gap-2">
                <div className="flex items-center gap-1">
                    <p className={STRIP_LABEL}>Forma de pago</p>
                    <FieldHelp label="Forma de pago" help={SALE_PROMOTION_HELP.paymentOptions} />
                </div>
                <span className="text-[10px] text-muted-foreground">
                    Sin nada marcado = cualquiera
                </span>
            </div>
            <PaymentOptionsPicker
                selected={selected}
                onChange={(next) => {
                    setValue('paymentOptions', next, { shouldDirty: true });
                }}
            />
        </div>
    );
}

/** El tope de una promo y lo que lleva gastado; lo comparten venta y referido. */
export function SalePromotionBudgetEditor({ consumedCents }: { consumedCents: number | null }) {
    return <BudgetBox consumedCents={consumedCents} />;
}

/** Reglas de una promo de venta: por cliente, si se suma con otras y con qué forma de pago. */
export function SalePromotionRulesEditor() {
    return (
        <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <SaleRules />
            </div>
            <PaymentOptionsBox />
        </>
    );
}

function SaleRules() {
    const { control, register, setValue } = useFormContext<SalePromotionFormValues>();
    const values = useWatch({ control }) as SalePromotionFormValues;
    const stackable = values.stackable ?? defaultStackable(values);
    return (
        <>
            <RuleBox
                label="Por cliente"
                help={SALE_PROMOTION_HELP.oncePerCustomer}
                note="Si ya la usó, no le vuelve a salir."
            >
                <label className={CHECK}>
                    <input
                        type="checkbox"
                        className="accent-primary"
                        {...register('oncePerCustomer')}
                    />
                    Una vez por cliente
                </label>
            </RuleBox>
            <RuleBox
                label="Con otras promos"
                help={SALE_PROMOTION_HELP.stackable}
                note="Apagada va sola: una por producto."
            >
                <label className={CHECK} title="Se suma con otras promos en el mismo producto">
                    <input
                        type="checkbox"
                        className="accent-primary"
                        checked={stackable}
                        onChange={(event) => {
                            setValue('stackable', event.target.checked, { shouldDirty: true });
                        }}
                    />
                    Acumulable
                </label>
            </RuleBox>
        </>
    );
}
